import React, { useMemo, useRef, useEffect, useCallback } from 'react';
import type { TranscriptEntry, SessionToolCall } from '@/lib/api/sessions';
import ToolCallBubble from '@/components/sessions/ToolCallBubble';

interface SessionTranscriptTimelineProps {
  transcripts: TranscriptEntry[];
  toolCalls: SessionToolCall[];
  className?: string;
  /** Current audio playback time in seconds (drives auto-scroll + highlight). */
  audioCurrentTime?: number;
  /** ISO timestamp of the session start — used to compute per-entry offsets. */
  sessionStartTime?: string;
  /** Called when a message is clicked — receives the time offset in seconds. */
  onSeek?: (timeInSeconds: number) => void;
  /** When true, tool call bubbles hide their inline media (media renders in ContentViewer instead). */
  hideToolCallMedia?: boolean;
}

type TimelineItem =
  | { kind: 'message'; data: TranscriptEntry }
  | { kind: 'tool_call'; data: SessionToolCall };

const isLeadSpeaker = (speaker: string) => speaker === 'user' || speaker === 'customer';

/** Return the ISO timestamp string for a timeline item. */
const getItemTimestamp = (item: TimelineItem): string | null =>
  item.kind === 'message' ? item.data.timestamp : item.data.created_at;

const SessionTranscriptTimeline: React.FC<SessionTranscriptTimelineProps> = ({
  transcripts,
  toolCalls,
  className,
  audioCurrentTime,
  sessionStartTime,
  onSeek,
  hideToolCallMedia,
}) => {
  const itemRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const lastScrolledIndex = useRef<number>(-1);

  const mergedTimeline = useMemo<TimelineItem[]>(() => {
    const items: TimelineItem[] = [];

    for (const msg of transcripts) {
      items.push({ kind: 'message', data: msg });
    }

    for (const tc of toolCalls) {
      items.push({ kind: 'tool_call', data: tc });
    }

    items.sort((a, b) => {
      const tsA = getItemTimestamp(a);
      const tsB = getItemTimestamp(b);
      if (!tsA && !tsB) return 0;
      if (!tsA) return 1;
      if (!tsB) return -1;
      return new Date(tsA).getTime() - new Date(tsB).getTime();
    });

    return items;
  }, [transcripts, toolCalls]);

  // Compute per-item offset (seconds from session start)
  const itemOffsets = useMemo(() => {
    if (!sessionStartTime) return null;
    const startMs = new Date(sessionStartTime).getTime();
    if (Number.isNaN(startMs)) return null;

    return mergedTimeline.map((item) => {
      const ts = getItemTimestamp(item);
      if (!ts) return null;
      const ms = new Date(ts).getTime();
      if (Number.isNaN(ms)) return null;
      return (ms - startMs) / 1000;
    });
  }, [mergedTimeline, sessionStartTime]);

  // Find which item is currently "active" based on audio playback time
  const activeIndex = useMemo(() => {
    if (audioCurrentTime == null || !itemOffsets) return -1;
    let best = -1;
    for (let i = 0; i < itemOffsets.length; i++) {
      const offset = itemOffsets[i];
      if (offset != null && offset <= audioCurrentTime) {
        best = i;
      }
    }
    return best;
  }, [audioCurrentTime, itemOffsets]);

  // Auto-scroll to the active item
  useEffect(() => {
    if (activeIndex < 0 || activeIndex === lastScrolledIndex.current) return;
    lastScrolledIndex.current = activeIndex;
    const el = itemRefs.current.get(activeIndex);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [activeIndex]);

  const setItemRef = useCallback((index: number, el: HTMLDivElement | null) => {
    if (el) {
      itemRefs.current.set(index, el);
    } else {
      itemRefs.current.delete(index);
    }
  }, []);

  const handleItemClick = useCallback((index: number) => {
    if (!onSeek || !itemOffsets) return;
    const offset = itemOffsets[index];
    if (offset != null) {
      onSeek(offset);
    }
  }, [onSeek, itemOffsets]);

  if (mergedTimeline.length === 0) return null;

  const syncEnabled = audioCurrentTime != null && itemOffsets != null;
  const seekable = !!onSeek && itemOffsets != null;

  return (
    <div className={className}>
      {mergedTimeline.map((item, index) => {
        const isActive = syncEnabled && index === activeIndex;

        if (item.kind === 'tool_call') {
          return (
            <div
              key={`tc-${item.data.id}`}
              ref={(el) => setItemRef(index, el)}
              className={`rounded-lg transition-colors duration-200 ${isActive ? 'bg-blue-50/60 dark:bg-blue-950/30 ring-1 ring-blue-300 dark:ring-blue-700' : ''} ${seekable ? 'cursor-pointer' : ''}`}
              onClick={seekable ? () => handleItemClick(index) : undefined}
            >
              <ToolCallBubble toolCall={item.data} isActive={isActive} hideMedia={hideToolCallMedia} />
            </div>
          );
        }
        const message = item.data;
        return (
          <div
            key={`msg-${message.id || index}`}
            ref={(el) => setItemRef(index, el)}
            className={`flex gap-3 rounded-lg transition-colors duration-200 ${isLeadSpeaker(message.speaker) ? 'justify-end' : 'justify-start'} ${isActive ? 'bg-blue-50/60 dark:bg-blue-950/30 ring-1 ring-blue-300 dark:ring-blue-700 p-1' : ''} ${seekable ? 'cursor-pointer' : ''}`}
            onClick={seekable ? () => handleItemClick(index) : undefined}
          >
            <div
              className={`max-w-[80%] rounded-lg px-3 py-2 shadow-sm ${
                isLeadSpeaker(message.speaker)
                  ? 'bg-blue-600 text-white'
                  : 'bg-white dark:bg-zinc-800 border card-border text-gray-900 dark:text-gray-100'
              }`}
            >
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-xs font-medium capitalize">
                  {isLeadSpeaker(message.speaker) ? 'Lead' : 'Agent'}
                </span>
                {message.timestamp && (
                  <span className="text-xs opacity-70">
                    {new Date(message.timestamp).toLocaleTimeString()}
                  </span>
                )}
              </div>
              <p className="text-sm whitespace-pre-wrap">{message.text}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SessionTranscriptTimeline;
